import { ApiError } from "@/lib/api";
import {
  DOCUMENT_RULES,
  documentRuleMessage,
  extensionOf,
  formatFileSize,
  screenDocument,
  type DocumentRule,
  type DocumentRules,
} from "@/lib/brokerDocumentTypes";

// What every upload of files to PMS shares (BRK-S361's loss runs, BRK-S362's
// answers to a quote's subjectivities): PMS's served rules said in a line and
// checked here first, a file's progress and its tile, and PMS's refusal of
// one file (`DOCUMENT_REJECTED`) read for its row.

/** PMS's upload rules in a line ("PDF, JPG or PNG, up to 10 MB each"), read
 *  from what it serves, never restated here (BRK-S145). */
export function rulesLine(rules: DocumentRules): string {
  const kinds = rules.allowedExtensions.map((ext) =>
    ext.replace(/^\./, "").toUpperCase(),
  );
  const list =
    kinds.length > 1
      ? `${kinds.slice(0, -1).join(", ")} or ${kinds[kinds.length - 1]}`
      : (kinds[0] ?? "Files");
  return `${list}, up to ${formatFileSize(rules.maxBytesPerFile)} each`;
}

/** A file's tile: its extension in capitals ("PDF"), or "FILE" when it has
 *  none short enough to fit. */
export function fileTile(filename: string): string {
  const ext = extensionOf(filename).slice(1).toUpperCase();
  return ext !== "" && ext.length <= 4 ? ext : "FILE";
}

/** An upload's progress in whole percent, never past 100. */
export function uploadPercent(loaded: number, total: number): number {
  return total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
}

/** The chosen files that go up, and those refused here first by PMS's served
 *  rules (`screenDocument`), counting the `held` already on file or on their
 *  way. A courtesy, not a gate: PMS checks every one again. */
export function screenFiles(
  files: readonly File[],
  rules: DocumentRules,
  held: number,
): { accepted: File[]; refused: { file: File; message: string }[] } {
  const accepted: File[] = [];
  const refused: { file: File; message: string }[] = [];
  for (const file of files) {
    const rejection = screenDocument(file, rules, {
      attachedCount: held + accepted.length,
    });
    if (rejection) refused.push({ file, message: rejection.message });
    else accepted.push(file);
  }
  return { accepted, refused };
}

/** PMS's refusal of one file (`DOCUMENT_REJECTED`, PMS-S334): which file,
 *  the rule it broke when this build can name it, and PMS's own sentence.
 *  Anything else (a drop, a 409, a 500) is a failure to retry, not a refusal,
 *  and reads as null. */
export interface DocumentRefusal {
  filename: string;
  rule: DocumentRule | null;
  message: string;
}

export function documentRefusal(err: unknown): DocumentRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const code =
    (body as { code?: unknown }).code ?? (details as { code?: unknown }).code;
  if (code !== "DOCUMENT_REJECTED") return null;
  const { filename, rule } = details as { filename?: unknown; rule?: unknown };
  if (typeof filename !== "string") return null;
  return {
    filename,
    rule:
      typeof rule === "string" &&
      (DOCUMENT_RULES as readonly string[]).includes(rule)
        ? (rule as DocumentRule)
        : null,
    message: err.message,
  };
}

/** A refusal as the broker reads it: the rule in the sentence the client's
 *  own check uses (`documentRuleMessage`), so the same broken rule never
 *  reads two ways; PMS's own sentence for a rule this build can't name, or
 *  before the rules are read. */
export function refusalMessage(
  refusal: DocumentRefusal,
  rules: DocumentRules | null,
): string {
  return refusal.rule && rules
    ? documentRuleMessage(refusal.rule, refusal.filename, rules)
    : refusal.message;
}
